import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import get from 'lodash/get';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import zipObject from 'lodash/zipObject';

import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import history from 'pages/router/history';
import { entities } from 'pages/store/schema';
import { getData, postData, putData, clearData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import {
  getProcessing,
  getErrors,
  getAllGatewayConfigurationParameters,
  getAvailableGatewayTypes,
  getAllGatewayCredentialParameters,
  getMappablePaymentTypes,
  getValues,
} from 'pages/store/selectors';
import { GatewayParameters } from 'pages/store/selectors/gateway';

import Layout, { LayoutSection } from 'cirrus/Layout';
import ViewError from 'pages/components/ViewError';

import GatewayType from '../GatewayType';
import GatewayName from '../GatewayName';
import SelectedShops from '../SelectedShops';
import GatewayCredentials from '../GatewayCredentials';
import GatewayConfiguration from '../GatewayConfiguration';
import PaymentTypeMapping from '../PaymentTypeMapping';

import { paths } from '../../routes';

const merchantos = window.merchantos;

const STRINGS = {
  SELECT_GATEWAY: {
    TITLE: 'Select Gateway',
    DESCRIPTION: 'Select your payment processor',
  },
  GATEWAY_NAME: {
    TITLE: 'Assign a Name',
    DESCRIPTION: 'You may assign a unique name to this gateway configuration',
  },
  GATEWAY_CREDENTIALS: {
    TITLE: 'Gateway Credentials',
    DESCRIPTION: 'You can get this info from your payment processor',
  },
  SELECTED_SHOPS: {
    TITLE: 'Select Shops',
    DESCRIPTION: 'Select the shops that will be using this gateway to process payments',
  },
  PAYMENT_MAPPING: {
    TITLE: 'Credit Card Payment Mapping',
    DESCRIPTION: 'Select how card brands map to your payment types',
  },
  ERROR: {
    SAVE_ERROR: {
      TITLE: 'Gateway Configuration not Saved',
      MESSAGE:
        'The gateway configuration could not be saved.  If this problem persists, please contact customer support.',
    },
    SAVE_CREDENTIALS_ERROR: {
      MESSAGE: 'Please fill out all of the required form fields.',
    },
  },
};

// uses the baked-in types defined by Retail
const DEFAULT_CREDIT_PAYMENT_TYPE_ID = '3';
const DEFAULT_DEBIT_PAYMENT_TYPE_ID = '6';

// matches the card schemes supported by Retail
const SUPPORTED_CARD_SCHEMES = [
  PaymentTransaction.SupportedCardType.VISA,
  PaymentTransaction.SupportedCardType.MASTERCARD,
  PaymentTransaction.SupportedCardType.DISCOVER,
  PaymentTransaction.SupportedCardType.AMERICAN_EXPRESS,
  PaymentTransaction.SupportedCardType.DEBIT,
  PaymentTransaction.SupportedCardType.INTERAC,
  PaymentTransaction.SupportedCardType.BANCONTACT,
  PaymentTransaction.SupportedCardType.IDEAL,
];

const getInitialCardMappings = () =>
  SUPPORTED_CARD_SCHEMES.map(cardScheme => ({
    cardSchemeID: cardScheme,
    name: cardScheme,
    paymentTypeID: [
      PaymentTransaction.SupportedCardType.DEBIT,
      PaymentTransaction.SupportedCardType.INTERAC,
      PaymentTransaction.SupportedCardType.BANCONTACT,
      PaymentTransaction.SupportedCardType.IDEAL,
    ].includes(cardScheme)
      ? DEFAULT_DEBIT_PAYMENT_TYPE_ID
      : DEFAULT_CREDIT_PAYMENT_TYPE_ID,
  }));

// populate Formik field values
export const formatInitialValues = ({
  availableGatewayTypes = [],
  gatewayCredentialParameters = [],
  gatewayConfigurationParameters = [],
  shops = [],
}) => {
  const gatewayCredentials = {};
  gatewayCredentialParameters.forEach(credentialParameters => {
    credentialParameters.parameters.forEach(parameter => {
      gatewayCredentials[parameter.id] = '';
    });
  });

  const gatewayConfiguration = {};
  gatewayConfigurationParameters.forEach(configurationParameters => {
    configurationParameters.parameters.forEach(parameter => {
      switch (parameter.id) {
        case GatewayParameters.ALLOW_CREDITS:
        case GatewayParameters.PARTIAL_APPROVALS:
        case GatewayParameters.SWIPERS_ENABLED:
        case GatewayParameters.MAGSTRIPE_FALLBACK:
          gatewayConfiguration[parameter.id] = false;
          break;
        case GatewayParameters.CHECK_DUPLICATES:
          gatewayConfiguration[parameter.id] = true;
          break;
        default:
          gatewayConfiguration[parameter.id] = '';
      }
    });
  });

  const cardMappings = getInitialCardMappings();

  const cardSchemeMappings = zipObject(
    cardMappings.map(mapping => `cardMapping-${get(mapping, 'cardSchemeID')}`),
    cardMappings.map(mapping => get(mapping, 'paymentTypeID'))
  );

  const selectedShops = zipObject(
    shops.map(shop => `shop_${get(shop, 'shopID')}`),
    shops.map(() => false)
  );

  return {
    gatewayID: get(availableGatewayTypes[0], 'gatewayID'),
    gatewayName: '',
    ...gatewayCredentials,
    ...selectedShops,
    ...gatewayConfiguration,
    ...cardSchemeMappings,
  };
};

class AddGatewayComponent extends Component {
  constructor(props) {
    super(props);
    merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            type: 'ui_tab',
            text: 'Settings',
            name: 'admin',
            icon: 'settings',
          },
          {
            text: 'Payment Processing',
            page: paths.paymentProcessing,
          },
          {
            text: 'Add Payment Gateway',
          },
        ],
      },
    });

    this.handleSave = this.handleSave.bind(this);
    this.renderGatewayCredentials = this.renderGatewayCredentials.bind(this);
  }

  componentDidMount() {
    this.props.getData();
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  handleSave(values = {}) {
    const {
      gatewayConfigurationParameters,
      gatewayCredentialParameters,
      cardMappings,
      shops,
    } = this.props;
    const { gatewayID, gatewayName } = values;

    // Formik requires a flat value structure so we need to prepare our data for transmission
    const credentialParameters = get(
      gatewayCredentialParameters.find(
        gatewayParameters => gatewayParameters.gatewayID === gatewayID
      ),
      'parameters',
      []
    );
    const configurationParameters = get(
      gatewayConfigurationParameters.find(
        gatewayParameters => gatewayParameters.gatewayID === gatewayID
      ),
      'parameters',
      []
    );

    const gatewayCredentials = {};
    credentialParameters.forEach(parameter => {
      gatewayCredentials[parameter.id] = get(values, parameter.id);
    });

    const gatewayConfiguration = {};
    configurationParameters.forEach(parameter => {
      gatewayConfiguration[parameter.id] = get(values, parameter.id);
    });

    const cardSchemeMappings = cardMappings.map(mapping => {
      const newPaymentTypeID = get(values, `cardMapping-${get(mapping, 'cardSchemeID')}`);

      return {
        ...mapping,
        paymentTypeID: newPaymentTypeID,
      };
    });

    const payload = {
      gatewayID,
      name: gatewayName,
      cardSchemeMappings: {
        CardSchemeMapping: cardSchemeMappings,
      },
      parameters: {
        ...gatewayCredentials,
        ...gatewayConfiguration,
      },
    };

    this.props.saveData(payload, response => {
      const { gatewayConfigID } = response.GatewayConfig;

      const selectedShops = shops.map(shop => {
        const selectedToThisShop = get(values, `shop_${get(shop, 'shopID')}`);

        return {
          ...shop,
          gatewayConfigID: selectedToThisShop ? gatewayConfigID : get(shop, 'gatewayConfigID'),
        };
      });

      selectedShops.forEach(shop =>
        this.props.saveShopData(shop.shopID, shop, () => {
          history.push(paths.paymentProcessing);
        })
      );
    });
  }

  renderGatewayCredentials() {
    const { isLoading, gatewayCredentialParameters } = this.props;

    return (
      <LayoutSection
        title={STRINGS.GATEWAY_CREDENTIALS.TITLE}
        description={STRINGS.GATEWAY_CREDENTIALS.DESCRIPTION}
      >
        <GatewayCredentials isLoading={isLoading} parameters={gatewayCredentialParameters} />
      </LayoutSection>
    );
  }

  render() {
    const {
      availableGatewayTypes,
      isLoading,
      isSaving,
      isErroring,
      gatewayCredentialParameters,
      gatewayConfigurationParameters,
      paymentTypes,
      cardMappings,
      saveErrors,
      shops,
    } = this.props;
    const initialValues = formatInitialValues(this.props);

    return (
      <ViewError isErroring={isErroring}>
        <Formik
          enableReinitialize
          onSubmit={this.handleSave}
          initialValues={initialValues}
          render={formikProps => {
            const { dirty, handleSubmit, values } = formikProps;
            const { gatewayID } = values;
            const credentialParameters = get(
              gatewayCredentialParameters.find(
                gatewayParameters => gatewayParameters.gatewayID === gatewayID
              ),
              'parameters',
              []
            );
            const configurationParameters = get(
              gatewayConfigurationParameters.find(
                gatewayParameters => gatewayParameters.gatewayID === gatewayID
              ),
              'parameters',
              []
            );

            return (
              <Fragment>
                <div className="cr-flex cr-justify-between cr-items-center cr-width-full cr-p-2 ">
                  <Button
                    variant="secondary"
                    fill
                    loading={isSaving}
                    disabled={!dirty || isLoading}
                    onClick={handleSubmit}
                    data-test="page-layout-save-button"
                  >
                    Save Changes
                  </Button>
                </div>
                <Divider />
                {!!saveErrors && (
                  <div
                    className="cr-max-width-content cr-display-block-centered"
                    data-test="settings-optional-modules-alert"
                  >
                    <Alert type="danger" title={STRINGS.ERROR.SAVE_ERROR.TITLE}>
                      {get(saveErrors, 'response.data.httpCode') === '422'
                        ? STRINGS.ERROR.SAVE_CREDENTIALS_ERROR.MESSAGE
                        : STRINGS.ERROR.SAVE_ERROR.MESSAGE}
                    </Alert>
                  </div>
                )}
                <Layout>
                  <LayoutSection
                    title={STRINGS.SELECT_GATEWAY.TITLE}
                    description={STRINGS.SELECT_GATEWAY.DESCRIPTION}
                  >
                    <GatewayType
                      isLoading={isLoading}
                      gatewayTypes={availableGatewayTypes}
                      fieldName="gatewayID"
                    />
                  </LayoutSection>
                  <Divider />
                  <LayoutSection
                    title={STRINGS.GATEWAY_NAME.TITLE}
                    description={STRINGS.GATEWAY_NAME.DESCRIPTION}
                  >
                    <GatewayName isLoading={isLoading} fieldName="gatewayName" />
                  </LayoutSection>
                </Layout>
                <Divider />
                {credentialParameters.length > 0 && (
                  <Fragment>
                    <LayoutSection
                      title={STRINGS.GATEWAY_CREDENTIALS.TITLE}
                      description={STRINGS.GATEWAY_CREDENTIALS.DESCRIPTION}
                    >
                      <GatewayCredentials isLoading={isLoading} parameters={credentialParameters} />
                    </LayoutSection>
                    <Divider />
                  </Fragment>
                )}
                <LayoutSection
                  title={STRINGS.SELECTED_SHOPS.TITLE}
                  description={STRINGS.SELECTED_SHOPS.DESCRIPTION}
                >
                  <SelectedShops isLoading={isLoading} shops={shops} />
                </LayoutSection>
                {configurationParameters.length > 0 && (
                  <GatewayConfiguration
                    isLoading={isLoading}
                    parameters={configurationParameters}
                  />
                )}
                <Divider />
                <LayoutSection
                  title={STRINGS.PAYMENT_MAPPING.TITLE}
                  description={STRINGS.PAYMENT_MAPPING.DESCRIPTION}
                >
                  <PaymentTypeMapping
                    paymentTypes={paymentTypes}
                    cardMappings={cardMappings}
                    isLoading={isLoading}
                  />
                </LayoutSection>
              </Fragment>
            );
          }}
        />
      </ViewError>
    );
  }
}

AddGatewayComponent.defaultProps = {
  isLoading: true,
  isErroring: false,
};

const mapStateToProps = state => ({
  gatewayConfigurationParameters: getAllGatewayConfigurationParameters(state),
  gatewayCredentialParameters: getAllGatewayCredentialParameters(state),
  availableGatewayTypes: getAvailableGatewayTypes(state),
  paymentTypes: getMappablePaymentTypes(state),
  shops: getValues(entities.shop)(state),
  cardMappings: getInitialCardMappings(),
  isErroring: !!(
    getErrors({ entity: entities.gateway, list: true, actionType: actionTypes.LOADING })(state) ||
    getErrors({ entity: entities.paymentType, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getErrors({ entity: entities.shop, list: true, actionType: actionTypes.LOADING })(state)
  ),
  isLoading: !!(
    getProcessing({ entity: entities.gateway, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.paymentType, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.shop, list: true, actionType: actionTypes.LOADING })(state)
  ),
  isSaving: !!getProcessing({
    entity: entities.gatewayConfig,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  saveErrors:
    getErrors({ entity: entities.gatewayConfig, list: false, actionType: actionTypes.SAVING })(
      state
    ) || getErrors({ entity: entities.shop, list: false, actionType: actionTypes.SAVING })(state),
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  getData: () => {
    dispatch(
      getData({
        url: `/API/Account/${merchantos.account.id}/Gateway.json`,
        entity: entities.gateway,
        list: true,
      })
    );
    dispatch(
      getData({
        url: `/API/Account/${merchantos.account.id}/PaymentType.json`,
        entity: entities.paymentType,
        list: true,
      })
    );
    dispatch(
      getData({
        url: `/API/Account/${merchantos.account.id}/Shop.json`,
        entity: entities.shop,
        list: true,
      })
    );
  },
  clearData: () => {
    dispatch(
      clearData([entities.gatewayConfig, entities.shop, entities.ccTerminal, entities.paymentType])
    );
  },
  saveData: (payload, onSuccess) => {
    dispatch(
      postData({
        url: `/API/Account/${merchantos.account.id}/GatewayConfig.json`,
        payload,
        entity: entities.gatewayConfig,
        onSuccess,
      })
    );
  },
  saveShopData: (shopId, payload, onSuccess) => {
    dispatch(
      putData({
        url: `/API/Account/${merchantos.account.id}/Shop/${shopId}.json`,
        payload,
        entity: entities.shop,
        onSuccess,
      })
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AddGatewayComponent);
